import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { EntityDropdownPickerAdapter } from '../entity-dropdown-picker/entity-dropdown-picker.model';

type PickerDisplayEntity = {
  __label: string;
  __secondaryLabel?: string;
};

@Component({
  selector: 'app-entity-dropdown-multiselect-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiSelectModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EntityDropdownMultiselectPickerComponent),
      multi: true,
    },
  ],
  templateUrl: './entity-dropdown-multiselect-picker.component.html',
  styleUrls: ['./entity-dropdown-multiselect-picker.component.css'],
})
export class EntityDropdownMultiselectPickerComponent<TOption, TValue extends TOption = TOption>
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input({ required: true }) adapter!: EntityDropdownPickerAdapter<TOption, TValue>;
  @Input() inputId = 'entity-multiselect-picker';
  @Input() placeholder = 'Select';
  @Input() label = 'Select';
  @Input() showClear = true;
  @Input() appendTo: 'body' | undefined = 'body';
  @Input() styleClass = 'w-full';
  @Input() panelStyleClass = 'w-full';
  @Input() filterPlaceholder = 'Filter';
  @Input() emptyMessage = 'No results found';
  @Input() columnClass = 'col-12 md:col-6 lg:col-4';
  @Input() addonIconClass = '';
  @Input() recentLimit = 10;
  @Input() searchPageSize = 25;
  @Input() searchMinLength = 1;
  @Input() searchDebounceMs = 250;
  @Input() dataKey = 'id';

  @Output() selectionLoaded = new EventEmitter<TValue[]>();
  @Output() pickerError = new EventEmitter<unknown>();

  options: Array<TOption & PickerDisplayEntity> = [];
  selectedValues: Array<TValue & PickerDisplayEntity> = [];
  isLoading = false;

  private querySubject = new Subject<string>();
  private querySubscription?: Subscription;
  private onChange: (value: TValue[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @HostBinding('class')
  get hostClass(): string {
    return `entity-dropdown-multiselect-picker field ${this.columnClass}`.trim();
  }

  ngOnInit(): void {
    this.querySubscription = this.querySubject
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
        switchMap((query) => this.loadOptionsForQuery(query))
      )
      .subscribe();
  }

  onDropdownShow(): void {
    this.loadRecentOptions();
  }

  onDropdownHide(): void {
    this.querySubject.next('');
  }

  onFilterInput(query: string): void {
    this.querySubject.next(query);
  }

  onSelectionChange(selection: Array<TOption & PickerDisplayEntity> | null): void {
    this.onTouched();

    const nextSelection = (selection || []) as Array<TValue & PickerDisplayEntity>;
    this.selectedValues = nextSelection;
    this.onChange(nextSelection as TValue[]);
    this.selectionLoaded.emit(nextSelection as TValue[]);
    this.touchSelection(nextSelection as TValue[]);
  }

  writeValue(value: TValue[] | null): void {
    const nextSelection = (value || []).map((item) => this.toDisplayEntity(item));
    this.selectedValues = nextSelection;
    if (nextSelection.length) {
      this.options = this.mergeSelectedIntoList(this.options, nextSelection);
    }
  }

  registerOnChange(fn: (value: TValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  disabled = false;

  getLabel(item: TOption | TValue): string {
    return this.toDisplayEntity(item).__label;
  }

  getSecondaryLabel(item: TOption | TValue): string {
    return this.toDisplayEntity(item).__secondaryLabel ?? '';
  }

  trackById = (_: number, item: TOption): string | number => this.adapter.getId(item);

  private loadRecentOptions(): void {
    this.isLoading = true;
    this.adapter.loadRecent(this.recentLimit).pipe(
      catchError((error) => {
        this.pickerError.emit(error);
        return of([] as TOption[]);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe({
      next: (recentItems: TOption[]) => {
        this.options = this.mergeSelectedIntoList(
          recentItems.map((item) => this.toDisplayEntity(item)),
          this.selectedValues
        );
      },
      error: () => undefined,
    });
  }

  private loadOptionsForQuery(query: string) {
    const normalizedQuery = query.trim();

    if (normalizedQuery.length < this.searchMinLength) {
      this.loadRecentOptions();
      return of(void 0);
    }

    this.isLoading = true;
    return this.adapter.search(normalizedQuery, 0, this.searchPageSize).pipe(
      catchError((error) => {
        this.pickerError.emit(error);
        return of({ content: [] } as { content: TOption[] });
      }),
      finalize(() => {
        this.isLoading = false;
      }),
      tap((page) => {
        this.options = this.mergeSelectedIntoList(
          (page?.content || []).map((item) => this.toDisplayEntity(item)),
          this.selectedValues
        );
      })
    );
  }

  private touchSelection(entities: TValue[]): void {
    entities.forEach((entity) => {
      const result = this.adapter.rememberSelection(entity);
      if (result instanceof Promise) {
        void result.catch((error) => this.pickerError.emit(error));
      } else if (result && typeof (result as any).subscribe === 'function') {
        void (result as any).pipe(catchError((error: unknown) => {
          this.pickerError.emit(error);
          return of(void 0);
        })).subscribe();
      }
    });
  }

  private mergeSelectedIntoList(
    items: Array<TOption & PickerDisplayEntity>,
    selectedItems: Array<TValue & PickerDisplayEntity> = this.selectedValues
  ): Array<TOption & PickerDisplayEntity> {
    if (!selectedItems.length) {
      return items;
    }

    const selectedIds = new Set(selectedItems.map((item) => this.adapter.getId(item)));
    const withoutSelected = items.filter((item) => !selectedIds.has(this.adapter.getId(item)));
    return [...selectedItems, ...withoutSelected];
  }

  private toDisplayEntity(entity: TOption | TValue): TValue & PickerDisplayEntity {
    const label = this.adapter.getLabel(entity);
    const secondaryLabel = this.adapter.getSecondaryLabel?.(entity) ?? '';
    return {
      ...(entity as TValue),
      __label: label || String(this.adapter.getId(entity) ?? ''),
      __secondaryLabel: secondaryLabel,
    };
  }

  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
  }
}
